import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { map } from 'rxjs';
import * as v from 'valibot';

import { runningInTestsToken } from '../injection-token';
import { jsonStringifyRedacted } from '../redaction';
import { getSchemaName } from './schema';

/**
 * Service responsible for validating data against valibot schemas.
 * It provides methods to parse data, handle validation errors, and determine
 * whether to hard fail or soft fail based on the global validation status.
 *
 * In future the validation status will be configurable via feature flags.
 */
@Injectable({
  providedIn: 'root',
})
export class ValidationService {
  protected readonly logger = inject(NGXLogger);
  private readonly inTestMode = inject(runningInTestsToken);

  /** Always use getValidationStatus to retrieve value */
  private validationStatus: ValidationStatus = ValidationStatus.NOT_SET;

  /**
   * Retrieves the current validation status.
   * Always use this method, this way HardFailValidationService can override the behavior.
   */
  getValidationStatus(): ValidationStatus {
    this.warnIfUsedInTests();
    return this.validationStatus;
  }

  /**
   * Depending on the status and configuration of the application, decides whether to globally hard fail on validation errors.
   */
  shouldHardFail(): boolean {
    return this.getValidationStatus() === ValidationStatus.HARD_FAIL;
  }

  /**
   * Parses the data with the provided schema.
   * Logs validation errors and throws error if validation fails.
   * If the global validation status is set to soft fail, it will not throw an error.
   * @param schema The valibot schema to validate against
   * @param data The data (object) to validate
   */
  parseWithSchema<T extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>>(
    schema: T,
    data: unknown,
  ): v.InferOutput<T> {
    try {
      return v.parse(schema, data);
    } catch (error) {
      this.logValidationError(schema, error, this.shouldHardFail(), data);

      if (this.shouldHardFail()) {
        throw error;
      }
      return data as v.InferOutput<T>;
    }
  }

  /**
   * Same as parseWithSchema, but does not throw an error if validation fails.
   * This function should be used if you never want to hard fail on validation errors.
   * @param schema The valibot schema to validate against
   * @param data The data (object) to validate
   */
  parseWithSchemaSoftFail<T extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>>(
    schema: T,
    data: unknown,
  ): v.InferOutput<T> {
    try {
      return v.parse(schema, data);
    } catch (error) {
      this.logValidationError(schema, error, false, data);
      return data as v.InferOutput<T>;
    }
  }

  /**
   * pipeResponse is meant to be used in a .pipe() and validates the received object.
   *
   * @example
   * this.http.get(...).pipe(
   *   validationService.pipeResponse(UserSchema),
   *   catchError(...),
   * )
   *
   * @param schema The valibot schema to validate against
   */
  pipeResponse<T extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>>(schema: T) {
    // parseWithSchema throws error if validation fails => pipeline will error out
    return map((response: unknown) => this.parseWithSchema(schema, response));
  }

  /**
   * Like pipeResponse, but does not throw an error if validation fails.
   * Instead, it only logs the validation error and returns the original data.
   *
   * @param schema The valibot schema to validate against
   */
  pipeResponseSoftFail<T extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>>(schema: T) {
    return map((response: unknown) => this.parseWithSchemaSoftFail(schema, response));
  }

  /**
   * Helper function that handles the logging of validation errors.
   * @param schema The valibot schema
   * @param error The error which occurred during validation
   * @param isHardFail Indicates if the validation failure is a hard or soft fail
   * @param response The original response that failed validation
   * @private
   */
  private logValidationError<T extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>>(
    schema: T,
    error: unknown,
    isHardFail: boolean,
    response: unknown,
  ) {
    if (!v.isValiError(error)) {
      this.logger.error('Unknown error during schema validation', error);
      return;
    }

    const schemaName = getSchemaName(schema);
    const failMode = isHardFail ? ValidationStatus.HARD_FAIL : ValidationStatus.SOFT_FAIL;

    const issueList = error.issues.map((issue) => `- ${v.getDotPath(issue)}: ${issue.message}`);

    const logEntry = [
      `#CDFTF0222 [${failMode}] Schema validation error: Schema(${schemaName})`,
      ...issueList,
      jsonStringifyRedacted(response),
      error.stack,
    ];

    this.logger.error(logEntry.join('\n'));
  }

  private warnIfUsedInTests() {
    if (this.inTestMode) {
      console.error(
        'WARNING: ValidationService should not be used in tests, consider using HardFailValidationService instead',
      );
    }
  }
}

/**
 * Enumeration representing the validation status.
 * The initial status is always NOT_SET, which should default to SOFT_FAIL handling in most cases.
 * After the configuration is loaded, the status can be set to HARD_FAIL or SOFT_FAIL.
 */
export enum ValidationStatus {
  NOT_SET = 'NOT_SET',
  HARD_FAIL = 'HARD_FAIL',
  SOFT_FAIL = 'SOFT_FAIL',
}
